import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Calendar, Users, ChevronDown } from "lucide-react";
import { api, WHATSAPP_LINK } from "../lib/api";

export default function BranchDetail() {
  const { slug } = useParams();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/branches/${slug}`)
      .then((r) => setBranch(r.data))
      .catch(() => setBranch(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="container-x py-32 text-center text-muted-foreground" data-testid="branch-loading">
        Yükleniyor...
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="container-x py-32 text-center" data-testid="branch-not-found">
        <h1 className="heading-lg">Branş bulunamadı</h1>
        <Link to="/branslar" className="btn-primary mt-6"><ArrowLeft className="h-4 w-4" /> Branşlara Dön</Link>
      </div>
    );
  }

  const isActive = branch.is_active;

  return (
    <div data-testid={`branch-detail-${slug}`}>
      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full bg-primary/15 blur-3xl" />
        <div className="container-x relative py-16 md:py-24">
          <Link to="/branslar" className="btn-ghost mb-6 -ml-2" data-testid="back-to-branches">
            <ArrowLeft className="h-4 w-4" /> Tüm Branşlar
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            {isActive ? (
              <span className="badge-status-active"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />Aktif Branş</span>
            ) : (
              <span className="badge-status-soon"><span className="h-1.5 w-1.5 rounded-full bg-primary" />Yakında Aktif</span>
            )}
          </div>
          <h1 className="heading-xl">{branch.name}</h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
            {branch.short_description}
          </p>
        </div>
      </section>

      {/* CONTENT */}
      {isActive ? <ActiveBranch branch={branch} openFaq={openFaq} setOpenFaq={setOpenFaq} /> : <SoonBranch branch={branch} />}
    </div>
  );
}

function SoonBranch({ branch }) {
  return (
    <section className="section-pad">
      <div className="container-x">
        <div className="card-elev p-8 md:p-12 max-w-3xl mx-auto text-center" data-testid="branch-soon-card">
          <div className="eyebrow mb-3 justify-center">Çalışma Aşamasında</div>
          <h2 className="heading-md mb-4">"Bu branş üzerinde çalışıyoruz."</h2>
          <p className="text-muted-foreground leading-relaxed">
            En kısa zamanda <span className="text-foreground font-semibold">Focus Spor Kulübü</span> çatısı
            altında <span className="text-primary font-semibold">{branch.name}</span> branşını aktif hale
            getirmeyi hedefliyoruz.
          </p>
          <div className="mt-8 p-5 rounded-lg border border-primary/30 bg-primary/5 text-left">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <span className="text-foreground font-semibold">Eğer bu branşın antrenörü, uzmanı ya da
              eğitmeni olduğunu düşünüyorsan</span> bizimle iletişime geç. Bu branşı birlikte en kısa zamanda
              aktif hale getirelim.
            </p>
          </div>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-primary" data-testid="branch-soon-whatsapp">
              <MessageCircle className="h-4 w-4" /> WhatsApp ile Başvur
            </a>
            <Link to="/antrenor-basvuru" className="btn-secondary" data-testid="branch-soon-coach-apply">
              Antrenör Başvuru Formu
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function ActiveBranch({ branch, openFaq, setOpenFaq }) {
  const faqs = [
    { q: "Ders saatlerini kaçırırsam ne olur?", a: "Telafi seansları için kulüp yönetimi ile iletişime geçebilirsin." },
    { q: "Lisanslı sporcu olmak zorunda mıyım?", a: "Hayır, başlangıç seviyesi için lisans şartı yoktur. Yarışmalara katılmak için lisans çıkarılır." },
    { q: "Hangi malzemeler gerekli?", a: "Branşa özel ekipman listesi kayıt aşamasında size verilir." },
  ];
  return (
    <section className="section-pad">
      <div className="container-x grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <div className="eyebrow mb-3">Branş Tanıtımı</div>
            <h2 className="heading-md">Neler öğreneceksin?</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {branch.long_description || `${branch.name} branşımızda temel teknikten ileri seviye uygulamalara kadar kapsamlı bir program sunuyoruz. Sporcular kendi seviyesine uygun gruplara ayrılarak güvenli bir ilerleme takip eder.`}
            </p>
          </div>
          <div>
            <div className="eyebrow mb-3">Sık Sorulan Sorular</div>
            <div className="divide-y divide-border border border-border rounded-lg">
              {faqs.map((f, i) => (
                <div key={i}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-secondary/50"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-display font-bold uppercase tracking-wider text-sm">{f.q}</span>
                    <ChevronDown className={`h-4 w-4 text-primary transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="card-elev p-6">
            <h3 className="font-display text-lg font-bold uppercase tracking-wider mb-4">Bilgi</h3>
            <ul className="space-y-3 text-sm">
              <InfoRow label="Yaş Grupları" value={(branch.age_groups || []).join(", ") || "Tüm Yaşlar"} />
              <InfoRow label="Eğitim Günleri" value={(branch.training_days || []).join(", ") || "Bilgi alınız"} />
              <InfoRow label="Antrenör" value={branch.coach_name || "Belirleniyor"} />
            </ul>
          </div>
          <div className="card-elev p-6">
            <h3 className="font-display text-lg font-bold uppercase tracking-wider mb-3">Hızlı Başvuru</h3>
            <p className="text-sm text-muted-foreground mb-4">Kayıt ve bilgi için doğrudan WhatsApp'tan ulaş.</p>
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-primary w-full" data-testid="branch-active-whatsapp">
              <MessageCircle className="h-4 w-4" /> WhatsApp ile İletişim
            </a>
            <Link to="/etkinlikler" className="btn-secondary w-full mt-3"><Calendar className="h-4 w-4" />Etkinliklere Bak</Link>
          </div>
        </aside>
      </div>
    </section>
  );
}

function InfoRow({ label, value }) {
  return (
    <li className="flex items-start gap-3">
      <Users className="h-4 w-4 mt-0.5 text-primary shrink-0" />
      <div>
        <div className="text-[10px] font-display uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <div>{value}</div>
      </div>
    </li>
  );
}
